import { groupBy } from "lodash"
import { useCallback, useMemo } from "react"

import {
  EVALUATION_STATUS,
  EvaluationConstructor,
  EvaluationProblemFlag,
} from "#customTypes/composedTypes/evaluationTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"

import { DASHBOARD_EVALUATION_STATUSES } from "./evaluationWorkloadDashboardConstants"

export type StatusCounts = Record<
  | EVALUATION_STATUS.NOT_STARTED
  | EVALUATION_STATUS.IN_PROGRESS
  | EVALUATION_STATUS.SUBMITTED
  | EVALUATION_STATUS.APPROVED
  | EVALUATION_STATUS.REJECTED
  | EVALUATION_STATUS.PRINTED,
  number
>

type DashboardStatus = keyof StatusCounts

export type TeacherWorkloadRow = {
  teacherId: Uuid
  teacherName: string
  total: number
  baselineTotal: number
  baselineCompleteCount: number
  statusCounts: StatusCounts
  problematicCount: number
  problemCount: number
  openCount: number
  completeCount: number
}

export type ApproverHealthRow = {
  approverId: Uuid
  approverName: string
  total: number
  complete: number
  baselineTotal: number
  baselineComplete: number
  ongoing: number
  problem: number
  statusCounts: StatusCounts
  problematicCount: number
}

const isDashboardStatus = (
  status: EVALUATION_STATUS
): status is DashboardStatus => DASHBOARD_EVALUATION_STATUSES.has(status)

const makeStatusCounts = (): StatusCounts => ({
  [EVALUATION_STATUS.NOT_STARTED]: 0,
  [EVALUATION_STATUS.IN_PROGRESS]: 0,
  [EVALUATION_STATUS.SUBMITTED]: 0,
  [EVALUATION_STATUS.APPROVED]: 0,
  [EVALUATION_STATUS.REJECTED]: 0,
  [EVALUATION_STATUS.PRINTED]: 0,
})

const getApproverBucket = (status: EVALUATION_STATUS) => {
  if (
    status === EVALUATION_STATUS.APPROVED ||
    status === EVALUATION_STATUS.PRINTED
  ) {
    return "complete"
  }
  if (
    status === EVALUATION_STATUS.SUBMITTED ||
    status === EVALUATION_STATUS.IN_PROGRESS
  ) {
    return "ongoing"
  }
  return "problem"
}

const getEvaluationApproverFilterId = (evaluation: EvaluationConstructor) =>
  evaluation.approverId ?? evaluation.approvedBy ?? null

const useEvaluationWorkloadDashboard = ({
  evaluationsById,
  usersById,
  statusFilters,
  teacherFilters,
  approverFilters,
  setTeacherFilters,
  setApproverFilters,
  problemFlagsByEvaluationId,
}: {
  evaluationsById: Record<Uuid, EvaluationConstructor>
  usersById: Record<Uuid, UserConstructor>
  statusFilters: string[]
  teacherFilters: string[]
  approverFilters: string[]
  setTeacherFilters: (userIds: string[]) => void
  setApproverFilters: (userIds: string[]) => void
  problemFlagsByEvaluationId: Record<Uuid, EvaluationProblemFlag>
}) => {
  const teacherFilterSet = useMemo(
    () => new Set<Uuid>(teacherFilters),
    [teacherFilters]
  )
  const statusFilterSet = useMemo(
    () => new Set<EVALUATION_STATUS>(statusFilters as EVALUATION_STATUS[]),
    [statusFilters]
  )
  const approverFilterSet = useMemo(
    () => new Set<Uuid>(approverFilters),
    [approverFilters]
  )

  const baselineEvaluations = useMemo(() => {
    return Object.values(evaluationsById).filter((evaluation) => {
      return (
        DASHBOARD_EVALUATION_STATUSES.has(evaluation.status) &&
        Boolean(evaluation.assignedTo)
      )
    })
  }, [evaluationsById])

  const teacherBaselineById = useMemo(() => {
    return baselineEvaluations.reduce<
      Record<Uuid, { total: number; completeCount: number }>
    >((acc, evaluation) => {
      const teacherId = evaluation.assignedTo
      if (!teacherId) return acc
      if (!acc[teacherId]) {
        acc[teacherId] = { total: 0, completeCount: 0 }
      }
      acc[teacherId].total += 1
      if (
        evaluation.status === EVALUATION_STATUS.APPROVED ||
        evaluation.status === EVALUATION_STATUS.PRINTED
      ) {
        acc[teacherId].completeCount += 1
      }
      return acc
    }, {})
  }, [baselineEvaluations])

  const approverBaselineById = useMemo(() => {
    return baselineEvaluations.reduce<
      Record<Uuid, { total: number; completeCount: number }>
    >((acc, evaluation) => {
      const approverId = getEvaluationApproverFilterId(evaluation)
      if (!approverId) return acc
      if (!acc[approverId]) {
        acc[approverId] = { total: 0, completeCount: 0 }
      }
      acc[approverId].total += 1
      if (
        evaluation.status === EVALUATION_STATUS.APPROVED ||
        evaluation.status === EVALUATION_STATUS.PRINTED
      ) {
        acc[approverId].completeCount += 1
      }
      return acc
    }, {})
  }, [baselineEvaluations])

  const scopedEvaluations = useMemo(() => {
    return Object.values(evaluationsById).filter((evaluation) => {
      if (!DASHBOARD_EVALUATION_STATUSES.has(evaluation.status)) return false
      if (!evaluation.assignedTo) return false

      const matchesStatus =
        !statusFilterSet.size || statusFilterSet.has(evaluation.status)
      const matchesTeacher =
        !teacherFilterSet.size || teacherFilterSet.has(evaluation.assignedTo)
      const approverFilterId = getEvaluationApproverFilterId(evaluation)
      const matchesApprover =
        !approverFilterSet.size ||
        (approverFilterId ? approverFilterSet.has(approverFilterId) : false)

      return matchesStatus && matchesTeacher && matchesApprover
    })
  }, [evaluationsById, statusFilterSet, teacherFilterSet, approverFilterSet])

  const teacherRows = useMemo(() => {
    const groupedByTeacher = groupBy(scopedEvaluations, "assignedTo")
    return Object.entries(groupedByTeacher)
      .map<TeacherWorkloadRow>(([teacherId, teacherEvals]) => {
        const statusCounts = makeStatusCounts()
        let problematicCount = 0
        let problemCount = 0

        for (const evaluation of teacherEvals) {
          if (!isDashboardStatus(evaluation.status)) continue
          statusCounts[evaluation.status] += 1
          const isProblematicFlagged = Boolean(
            problemFlagsByEvaluationId[evaluation.id]?.isProblematic
          )
          if (isProblematicFlagged) {
            problematicCount += 1
          }
          if (
            evaluation.status === EVALUATION_STATUS.REJECTED ||
            evaluation.status === EVALUATION_STATUS.NOT_STARTED ||
            isProblematicFlagged
          ) {
            problemCount += 1
          }
        }
        const openCount =
          statusCounts[EVALUATION_STATUS.IN_PROGRESS] +
          statusCounts[EVALUATION_STATUS.SUBMITTED]
        const completeCount =
          statusCounts[EVALUATION_STATUS.APPROVED] +
          statusCounts[EVALUATION_STATUS.PRINTED]
        const baselineSummary = teacherBaselineById[teacherId]

        return {
          teacherId,
          teacherName: usersById[teacherId]?.name ?? `Unknown (${teacherId})`,
          total: teacherEvals.length,
          baselineTotal: baselineSummary?.total ?? 0,
          baselineCompleteCount: baselineSummary?.completeCount ?? 0,
          statusCounts,
          problematicCount,
          problemCount,
          openCount,
          completeCount,
        }
      })
      .sort((a, b) => {
        // Priority 1: anyone with problems always comes first.
        const aHasProblems = a.problemCount > 0
        const bHasProblems = b.problemCount > 0
        if (aHasProblems !== bHasProblems) {
          return bHasProblems ? 1 : -1
        }

        // Priority 2: among same problem presence, unfinished/open presence.
        const aHasOpen = a.openCount > 0
        const bHasOpen = b.openCount > 0
        if (aHasOpen !== bHasOpen) {
          return bHasOpen ? 1 : -1
        }

        // Priority 3: more problems first.
        if (b.problemCount !== a.problemCount) {
          return b.problemCount - a.problemCount
        }

        // Priority 4: more open items first.
        if (b.openCount !== a.openCount) {
          return b.openCount - a.openCount
        }

        // Priority 5: total workload.
        if (b.total !== a.total) return b.total - a.total

        // Priority 6: complete count (lighter priority signal).
        if (b.completeCount !== a.completeCount) {
          return b.completeCount - a.completeCount
        }

        return a.teacherName.localeCompare(b.teacherName)
      })
  }, [
    problemFlagsByEvaluationId,
    scopedEvaluations,
    teacherBaselineById,
    usersById,
  ])

  const approverRows = useMemo(() => {
    const byApprover = groupBy(
      scopedEvaluations.filter((evaluation) =>
        Boolean(getEvaluationApproverFilterId(evaluation))
      ),
      (evaluation) => getEvaluationApproverFilterId(evaluation) ?? ""
    )
    return Object.entries(byApprover)
      .map<ApproverHealthRow>(([approverId, approverEvals]) => {
        const statusCounts = makeStatusCounts()
        let complete = 0
        let ongoing = 0
        let problem = 0
        let problematicCount = 0

        for (const evaluation of approverEvals) {
          if (!isDashboardStatus(evaluation.status)) continue
          statusCounts[evaluation.status] += 1
          const bucket = getApproverBucket(evaluation.status)
          if (bucket === "complete") complete += 1
          if (bucket === "ongoing") ongoing += 1
          if (bucket === "problem") problem += 1
          if (problemFlagsByEvaluationId[evaluation.id]?.isProblematic) {
            problematicCount += 1
          }
        }
        const baselineSummary = approverBaselineById[approverId]

        return {
          approverId,
          approverName:
            usersById[approverId]?.name ?? `Unknown (${approverId})`,
          total: approverEvals.length,
          complete,
          baselineTotal: baselineSummary?.total ?? 0,
          baselineComplete: baselineSummary?.completeCount ?? 0,
          ongoing,
          problem,
          statusCounts,
          problematicCount,
        }
      })
      .sort((a, b) => {
        if (b.problem !== a.problem) return b.problem - a.problem
        if (b.problematicCount !== a.problematicCount) {
          return b.problematicCount - a.problematicCount
        }
        if (b.total !== a.total) return b.total - a.total
        return a.approverName.localeCompare(b.approverName)
      })
  }, [
    approverBaselineById,
    problemFlagsByEvaluationId,
    scopedEvaluations,
    usersById,
  ])

  const toggleTeacherFilter = useCallback(
    (teacherId: Uuid) => {
      const isActive =
        teacherFilters.length === 1 && teacherFilters[0] === teacherId
      setTeacherFilters(isActive ? [] : [teacherId])
    },
    [setTeacherFilters, teacherFilters]
  )

  const toggleApproverFilter = useCallback(
    (approverId: Uuid) => {
      const isActive =
        approverFilters.length === 1 && approverFilters[0] === approverId
      setApproverFilters(isActive ? [] : [approverId])
    },
    [approverFilters, setApproverFilters]
  )

  return {
    teacherRows,
    approverRows,
    scopedEvalCount: scopedEvaluations.length,
    toggleTeacherFilter,
    toggleApproverFilter,
  }
}

export default useEvaluationWorkloadDashboard
